<template>
    <div class="sr-page">
        <!-- Header -->
        <div class="sr-header">
            <div class="sr-header-left">
                <router-link :to="`/payroll-history/${payrollId}`" class="sr-back-btn">
                    <i class="fas fa-arrow-left"></i>
                </router-link>
                <div class="sr-icon-wrap"><i class="fas fa-file-invoice"></i></div>
                <div>
                    <h2 class="sr-title">Salary Report</h2>
                    <span class="sr-subtitle" v-if="company">{{ company.name }}</span>
                </div>
            </div>
            <div class="sr-header-right" v-if="payslips.length">
                <button class="sr-export-btn sr-btn-csv" @click="exportToCSV" title="Export to CSV">
                    <i class="fas fa-file-csv"></i> CSV
                </button>
                <button class="sr-export-btn sr-btn-pdf" @click="exportToPDF" title="Export to PDF">
                    <i class="fas fa-file-pdf"></i> PDF
                </button>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="sr-loading"><div class="sr-spinner"></div><span>Loading...</span></div>

        <!-- Content -->
        <template v-else-if="payslips.length">
            <!-- Summary Info -->
            <div class="sr-info-box">
                <div class="sr-info-row">
                    <span class="sr-info-label">Company:</span>
                    <span class="sr-info-val">{{ company?.name }}</span>
                </div>
                <div class="sr-info-row">
                    <span class="sr-info-label">Period:</span>
                    <span class="sr-info-val">{{ period }}</span>
                </div>
                <div class="sr-info-row">
                    <span class="sr-info-label">From:</span>
                    <span class="sr-info-val">{{ payrollPayment?.startDate }}</span>
                </div>
                <div class="sr-info-row">
                    <span class="sr-info-label">To:</span>
                    <span class="sr-info-val">{{ payrollPayment?.endDate }}</span>
                </div>
                <div class="sr-info-row">
                    <span class="sr-info-label">Total Employees:</span>
                    <span class="sr-info-val">{{ payslips.length }}</span>
                </div>
                <div class="sr-info-row">
                    <span class="sr-info-label">Generated:</span>
                    <span class="sr-info-val">{{ generatedDateTime }}</span>
                </div>
            </div>

            <!-- Salary Details Table -->
            <div class="sr-table-wrap">
                <table class="sr-table">
                    <thead>
                        <tr>
                            <th class="sr-th-idx">#</th>
                            <th class="sr-th-name">Employee Name</th>
                            <th class="sr-th-num">Basic</th>
                            <th class="sr-th-num" v-for="head in earningHeads" :key="`earn-${head}`">{{ head }}</th>
                            <th class="sr-th-num sr-total">Total Earnings</th>
                            <th class="sr-th-num" v-for="head in deductionHeads" :key="`ded-${head}`">{{ head }}</th>
                            <th class="sr-th-num sr-total">Total Deductions</th>
                            <th class="sr-th-num sr-net">Net Salary</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(slip, idx) in payslips" :key="slip.id || idx">
                            <td class="sr-td-idx">{{ idx + 1 }}</td>
                            <td class="sr-td-name">{{ slip.employeeName }}</td>
                            <td class="sr-td-num">₹{{ formatNum(slip.basicSalary) }}</td>
                            <td class="sr-td-num" v-for="head in earningHeads" :key="`earn-${head}`">
                                ₹{{ formatNum(slip[head] || slip.earningDetails?.[head] || 0) }}
                            </td>
                            <td class="sr-td-num sr-total sr-earn">₹{{ formatNum(slip.totalEarningAmount) }}</td>
                            <td class="sr-td-num" v-for="head in deductionHeads" :key="`ded-${head}`">
                                ₹{{ formatNum(slip[head] || slip.deductionDetails?.[head] || 0) }}
                            </td>
                            <td class="sr-td-num sr-total sr-ded">₹{{ formatNum(slip.totalDeductionAmount) }}</td>
                            <td class="sr-td-num sr-net">₹{{ formatNum(slip.netSalaryAmount) }}</td>
                        </tr>
                        <tr class="sr-total-row">
                            <td colspan="2" class="sr-total-label">TOTAL</td>
                            <td class="sr-td-num sr-total-val">₹{{ formatNum(totals.basic) }}</td>
                            <td class="sr-td-num sr-total-val" v-for="head in earningHeads" :key="`total-earn-${head}`">
                                ₹{{ formatNum(totals.earnings[head] || 0) }}
                            </td>
                            <td class="sr-td-num sr-total sr-total-val sr-earn">₹{{ formatNum(totals.totalEarnings) }}</td>
                            <td class="sr-td-num sr-total-val" v-for="head in deductionHeads" :key="`total-ded-${head}`">
                                ₹{{ formatNum(totals.deductions[head] || 0) }}
                            </td>
                            <td class="sr-td-num sr-total sr-total-val sr-ded">₹{{ formatNum(totals.totalDeductions) }}</td>
                            <td class="sr-td-num sr-net sr-total-val">₹{{ formatNum(totals.netSalary) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- Empty State -->
        <div v-else class="sr-empty">
            <div class="sr-empty-icon"><i class="fas fa-file-invoice"></i></div>
            <h3>No salary data found</h3>
        </div>
    </div>
</template>

<script>
import { getMonthName } from "../../utils/common"
import { http, serverURL } from "../../utils/http"

export default {
    data() {
        return {
            payrollId: null,
            company: null,
            payrollPayment: null,
            payslips: [],
            earningHeads: [],
            deductionHeads: [],
            loading: true,
            generatedDateTime: '',
            // Common payhead field names
            commonEarnings: ['basicSalary', 'da', 'dearness', 'hra', 'housingAllowance', 'ta', 'travelAllowance', 'conveyance', 'medical', 'medicalAllowance', 'bonus', 'commission', 'performance', 'incentive', 'gratuity', 'overtime', 'overtimeAmount', 'allowance', 'special', 'arrears', 'leave_encashment', 'addition'],
            commonDeductions: ['pt', 'professionalTax', 'pf', 'provident', 'esic', 'esi', 'incomeTax', 'tax', 'loan', 'advance', 'lwf', 'labourWelfare', 'deduction', 'other_deduction', 'fine', 'recovery'],
        }
    },
    computed: {
        period() {
            if (!this.payrollPayment) return ''
            return `${getMonthName(this.payrollPayment.month)} ${this.payrollPayment.year}`
        },
        totals() {
            const totals = {
                basic: 0,
                earnings: {},
                totalEarnings: 0,
                deductions: {},
                totalDeductions: 0,
                netSalary: 0,
            }

            this.earningHeads.forEach(head => { totals.earnings[head] = 0 })
            this.deductionHeads.forEach(head => { totals.deductions[head] = 0 })

            this.payslips.forEach(slip => {
                totals.basic += slip.basicSalary || 0
                totals.totalEarnings += slip.totalEarningAmount || 0
                totals.totalDeductions += slip.totalDeductionAmount || 0
                totals.netSalary += slip.netSalaryAmount || 0

                this.earningHeads.forEach(head => {
                    totals.earnings[head] += (slip[head] || slip.earningDetails?.[head] || 0)
                })
                this.deductionHeads.forEach(head => {
                    totals.deductions[head] += (slip[head] || slip.deductionDetails?.[head] || 0)
                })
            })

            return totals
        },
    },
    mounted() {
        this.payrollId = this.$route.params.id
        this.generatedDateTime = new Date().toLocaleDateString('en-IN') + ' ' + new Date().toLocaleTimeString('en-IN')
        this.fetchData()
    },
    methods: {
        formatNum(v) { if (v == null) return '0'; return Number(v).toLocaleString('en-IN') },
        async fetchData() {
            this.loading = true
            try {
                const res = await http.get(`/payroll-payments/${this.payrollId}`)
                const { company, payrollPayment, payrollPaymentSlips } = res.data.result
                this.company = company
                this.payrollPayment = payrollPayment
                this.payslips = payrollPaymentSlips || []

                // Extract all payhead fields from slips
                const earningSet = new Set()
                const deductionSet = new Set()
                const excludeFields = ['id', 'name', 'employeeName', 'employeeId', 'ctc', 'totalLeaves', 'totalOvertimeHours', 'totalEarningAmount', 'totalDeductionAmount', 'netSalaryAmount', 'createdAt', 'updatedAt', 'earningDetails', 'deductionDetails']

                this.payslips.forEach(slip => {
                    // Check nested details objects first
                    if (slip.earningDetails && typeof slip.earningDetails === 'object') {
                        Object.keys(slip.earningDetails).forEach(key => earningSet.add(key))
                    }
                    if (slip.deductionDetails && typeof slip.deductionDetails === 'object') {
                        Object.keys(slip.deductionDetails).forEach(key => deductionSet.add(key))
                    }

                    // Check direct fields on slip object
                    Object.keys(slip).forEach(key => {
                        const val = slip[key]
                        if (!excludeFields.includes(key) && val !== null && val !== undefined && !isNaN(val) && val !== '') {
                            const keyLower = key.toLowerCase()

                            // Check against common earnings list
                            const isCommonEarning = this.commonEarnings.some(e => keyLower.includes(e.toLowerCase()) || e.toLowerCase().includes(keyLower))
                            if (isCommonEarning) {
                                earningSet.add(key)
                                return
                            }

                            // Check against common deductions list
                            const isCommonDeduction = this.commonDeductions.some(d => keyLower.includes(d.toLowerCase()) || d.toLowerCase().includes(keyLower))
                            if (isCommonDeduction) {
                                deductionSet.add(key)
                                return
                            }

                            // Fallback: Pattern matching for unmatched payheads
                            if (keyLower.includes('allowance') || keyLower.includes('bonus') || keyLower.includes('commission') ||
                                keyLower.includes('addition') || keyLower.includes('earning') || keyLower.includes('pay')) {
                                earningSet.add(key)
                            } else if (keyLower.includes('deduction') || keyLower.includes('tax') || keyLower.includes('pf') ||
                                       keyLower.includes('advance') || keyLower.includes('recovery')) {
                                deductionSet.add(key)
                            }
                        }
                    })
                })

                // Remove basicSalary from earningHeads since it's shown separately
                earningSet.delete('basicSalary')

                this.earningHeads = Array.from(earningSet).sort()
                this.deductionHeads = Array.from(deductionSet).sort()
            } catch (err) { console.log("error fetching salary report", err) }
            this.loading = false
        },
        exportToCSV() {
            let csv = `SALARY REPORT FOR AUDIT\n\n`
            csv += `Company Name,${this.company?.name}\n`
            csv += `Period,${this.period}\n`
            csv += `From Date,${this.payrollPayment?.startDate}\n`
            csv += `To Date,${this.payrollPayment?.endDate}\n`
            csv += `Generated Date & Time,${this.generatedDateTime}\n`
            csv += `Total Employees,${this.payslips.length}\n\n`

            const headers = ['#', 'Employee Name', 'Basic', ...this.earningHeads, 'Total Earnings', ...this.deductionHeads, 'Total Deductions', 'Net Salary']
            csv += headers.join(',') + '\n'

            this.payslips.forEach((slip, idx) => {
                const row = [idx + 1, slip.employeeName, slip.basicSalary || 0]
                this.earningHeads.forEach(head => { row.push(slip[head] || slip.earningDetails?.[head] || 0) })
                row.push(slip.totalEarningAmount || 0)
                this.deductionHeads.forEach(head => { row.push(slip[head] || slip.deductionDetails?.[head] || 0) })
                row.push(slip.totalDeductionAmount || 0, slip.netSalaryAmount || 0)
                csv += row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',') + '\n'
            })

            csv += '\n' + headers.join(',') + '\n'
            const totalsRow = ['TOTAL', '', this.totals.basic]
            this.earningHeads.forEach(head => { totalsRow.push(this.totals.earnings[head] || 0) })
            totalsRow.push(this.totals.totalEarnings)
            this.deductionHeads.forEach(head => { totalsRow.push(this.totals.deductions[head] || 0) })
            totalsRow.push(this.totals.totalDeductions, this.totals.netSalary)
            csv += totalsRow.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',') + '\n'

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', `salary-report-${this.company?.name || 'audit'}-${new Date().toISOString().slice(0, 10)}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        exportToPDF() {
            let tableHTML = '<table border="1" cellpadding="6" cellspacing="0" style="width:100%; border-collapse:collapse; font-family:Arial,sans-serif; font-size:11px;">'
            tableHTML += '<thead style="background-color:#f0f0f0;"><tr>'
            const headers = ['#', 'Employee Name', 'Basic', ...this.earningHeads, 'Total Earnings', ...this.deductionHeads, 'Total Deductions', 'Net Salary']
            headers.forEach(h => { tableHTML += `<th style="padding:8px; text-align:center; border:1px solid #ddd; font-weight:bold;">${h}</th>` })
            tableHTML += '</tr></thead><tbody>'

            this.payslips.forEach((slip, idx) => {
                const bgColor = idx % 2 === 0 ? '#ffffff' : '#f9f9f9'
                tableHTML += `<tr style="background-color:${bgColor};"><td style="padding:6px; border:1px solid #ddd; text-align:center;">${idx + 1}</td>`
                tableHTML += `<td style="padding:6px; border:1px solid #ddd;">${slip.employeeName}</td>`
                tableHTML += `<td style="padding:6px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(slip.basicSalary)}</td>`
                this.earningHeads.forEach(head => {
                    tableHTML += `<td style="padding:6px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(slip[head] || slip.earningDetails?.[head] || 0)}</td>`
                })
                tableHTML += `<td style="padding:6px; border:1px solid #ddd; text-align:right; background:#e8f5e9; font-weight:bold;">₹${this.formatNum(slip.totalEarningAmount)}</td>`
                this.deductionHeads.forEach(head => {
                    tableHTML += `<td style="padding:6px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(slip[head] || slip.deductionDetails?.[head] || 0)}</td>`
                })
                tableHTML += `<td style="padding:6px; border:1px solid #ddd; text-align:right; background:#ffebee; font-weight:bold;">₹${this.formatNum(slip.totalDeductionAmount)}</td>`
                tableHTML += `<td style="padding:6px; border:1px solid #ddd; text-align:right; background:#e3f2fd; font-weight:bold;">₹${this.formatNum(slip.netSalaryAmount)}</td></tr>`
            })

            tableHTML += '<tr style="background-color:#f0f0f0; font-weight:bold;"><td colspan="2" style="padding:8px; border:1px solid #ddd;">TOTAL</td>'
            tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(this.totals.basic)}</td>`
            this.earningHeads.forEach(head => {
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(this.totals.earnings[head] || 0)}</td>`
            })
            tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right; background:#e8f5e9;">₹${this.formatNum(this.totals.totalEarnings)}</td>`
            this.deductionHeads.forEach(head => {
                tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right;">₹${this.formatNum(this.totals.deductions[head] || 0)}</td>`
            })
            tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right; background:#ffebee;">₹${this.formatNum(this.totals.totalDeductions)}</td>`
            tableHTML += `<td style="padding:8px; border:1px solid #ddd; text-align:right; background:#e3f2fd;">₹${this.formatNum(this.totals.netSalary)}</td></tr></tbody></table>`

            let metadataHTML = '<div style="margin-bottom:15px; border-bottom:2px solid #ddd; padding-bottom:10px;">'
            metadataHTML += `<p style="margin:3px 0; font-size:13px;"><strong>Company:</strong> ${this.company?.name}</p>`
            metadataHTML += `<p style="margin:3px 0; font-size:13px;"><strong>Period:</strong> ${this.period}</p>`
            metadataHTML += `<p style="margin:3px 0; font-size:13px;"><strong>From:</strong> ${this.payrollPayment?.startDate} | <strong>To:</strong> ${this.payrollPayment?.endDate}</p>`
            metadataHTML += `<p style="margin:3px 0; font-size:13px;"><strong>Total Employees:</strong> ${this.payslips.length}</p>`
            metadataHTML += `<p style="margin:3px 0; font-size:13px;"><strong>Generated:</strong> ${this.generatedDateTime}</p>`
            metadataHTML += '</div>'

            const title = `Salary Report - ${this.company?.name}`
            const printWindow = window.open('', '', 'height=600,width=1200')
            printWindow.document.write(`<html><head><title>${title}</title><style>body{font-family:Arial,sans-serif; margin:15px;} h1{text-align:center; font-size:18px; margin-bottom:5px;} .subtitle{text-align:center; color:#666; font-size:14px; margin-bottom:15px; font-weight:bold;} table{margin-top:10px;} @media print{body{margin:10px;} table{font-size:10px;}}</style></head><body>`)
            printWindow.document.write(`<h1>${this.company?.name}</h1>`)
            printWindow.document.write(`<div class="subtitle">SALARY REPORT FOR AUDIT</div>`)
            printWindow.document.write(metadataHTML)
            printWindow.document.write(tableHTML)
            printWindow.document.write('</body></html>')
            printWindow.document.close()
            setTimeout(() => { printWindow.print() }, 250)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.sr-page { animation: srFade .3s ease; }
@keyframes srFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.sr-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.sr-header-left { display:flex; align-items:center; gap:10px; }
.sr-header-right { display:flex; align-items:center; gap:8px; }
.sr-back-btn { width:32px; height:32px; border-radius:8px; border:1px solid $gray200; display:flex; align-items:center; justify-content:center; color:$gray500; font-size:13px; transition:all $transitionFast; text-decoration:none; &:hover { background:$gray50; color:$primaryColor; border-color:$primaryColor; } }
.sr-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.sr-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.sr-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }
.sr-export-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border:1px solid $gray200; border-radius:6px; background:white; font-size:12px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; i { font-size:10px; } }
.sr-btn-csv { color:#16a34a; &:hover { background:#f0fdf4; border-color:rgba(22,163,74,0.3); } }
.sr-btn-pdf { color:#dc2626; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.3); } }

/* Info Box */
.sr-info-box { background:white; border:1px solid $gray100; border-radius:8px; padding:14px 16px; margin-bottom:12px; box-shadow:$shadowSm; display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 1fr)); gap:12px; }
.sr-info-row { display:flex; flex-direction:column; gap:4px; padding:8px; background:$gray50; border-radius:6px; border:1px solid $gray100; }
.sr-info-label { font-size:11px; color:$gray400; font-weight:600; text-transform:uppercase; letter-spacing:0.3px; }
.sr-info-val { font-size:13px; color:$gray800; font-weight:600; }

/* Table */
.sr-table-wrap { background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; overflow-x:auto; }
.sr-table { width:100%; border-collapse:collapse; font-size:12px; }
.sr-table thead { background:$gray50; border-bottom:2px solid $gray100; position:sticky; top:0; }
.sr-table th { padding:10px 8px; font-size:10px; font-weight:700; color:$gray600; text-transform:uppercase; letter-spacing:0.3px; white-space:nowrap; border-right:1px solid $gray100; &:last-child { border-right:none; } }
.sr-table td { padding:8px; color:$gray700; font-weight:500; border-bottom:1px solid $gray100; border-right:1px solid $gray100; white-space:nowrap; &:last-child { border-right:none; } }
.sr-table tbody tr { transition:background $transitionFast; &:hover { background:$gray50; } &:last-child td { border-bottom:none; } }
.sr-th-idx, .sr-td-idx { width:40px; text-align:center; color:$gray400; font-size:10px; }
.sr-th-name, .sr-td-name { min-width:140px; text-align:left; font-weight:600; }
.sr-th-num, .sr-td-num { text-align:right; min-width:90px; }
.sr-total { background:#e8f5e9 !important; color:#2d5016; font-weight:700; }
.sr-ded { background:#ffebee !important; color:#7d1b1b; }
.sr-net { background:#e3f2fd !important; color:#0d47a1; font-weight:700; }
.sr-earn { color:#2d5016; }
.sr-total-row { background:#f0f0f0; font-weight:700; }
.sr-total-label { padding:10px 8px; text-align:right; }
.sr-total-val { background:inherit; border-color:#ccc; }

/* Loading / Empty */
.sr-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.sr-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:srSpin .7s linear infinite; }
@keyframes srSpin { to { transform:rotate(360deg); } }
.sr-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.sr-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }

/* Responsive */
@media(max-width:1200px) { .sr-table-wrap { overflow-x:auto; } }
@media(max-width:992px) { .sr-header { flex-direction:column; align-items:stretch; } .sr-header-right { justify-content:flex-end; } .sr-info-box { grid-template-columns:repeat(2, 1fr); } }
@media(max-width:768px) { .sr-info-box { grid-template-columns:1fr; } }
@media(max-width:480px) { .sr-header-right { flex-direction:column; } .sr-export-btn { width:100%; justify-content:center; } .sr-table { font-size:10px; } .sr-table th { padding:6px 4px; font-size:9px; } .sr-table td { padding:6px 4px; } }
</style>
